import { LevelConfig } from '../config.ts';

export class RenderSystem {
  private virtualWidth: number;
  private virtualHeight: number;
  private shakeTimer: number = 0;
  private shakeIntensity: number = 0;
  private customBgs: Map<string, HTMLImageElement> = new Map();
  private animTimer: number = 0;

  // Offscreen pre-rendered static background caches
  private cachedNightclub: HTMLCanvasElement | null = null;
  private cachedPenthouse: HTMLCanvasElement | null = null;
  private cachedIbiza: HTMLCanvasElement | null = null;

  // Level transition banner
  private bannerText: string = '';
  private bannerSubtext: string = '';
  private bannerColor: string = '#ffffff';
  private bannerAlpha: number = 0;
  private bannerScale: number = 1.0;

  constructor(virtualWidth: number, virtualHeight: number) {
    this.virtualWidth = virtualWidth;
    this.virtualHeight = virtualHeight;
    this.rebuildStaticBgCaches();
  }

  public resize(virtualWidth: number, virtualHeight: number): void {
    if (this.virtualWidth !== virtualWidth || this.virtualHeight !== virtualHeight) {
      this.virtualWidth = virtualWidth;
      this.virtualHeight = virtualHeight;
      this.rebuildStaticBgCaches();
    }
  }

  public registerBgImage(theme: string, src: string): void {
    const img = new Image();
    img.src = src;
    this.customBgs.set(theme, img);
  }

  public triggerScreenShake(duration: number = 0.45, intensity: number = 14): void {
    this.shakeTimer = duration;
    this.shakeIntensity = intensity;
  }

  public triggerLevelBanner(levelName: string, subtext: string, color: string): void {
    this.bannerText = levelName;
    this.bannerSubtext = subtext;
    this.bannerColor = color;
    this.bannerAlpha = 1.0;
    this.bannerScale = 1.35;
  }

  public update(dt: number): void {
    this.animTimer += dt;
    if (this.shakeTimer > 0) {
      this.shakeTimer -= dt;
      if (this.shakeTimer <= 0) {
        this.shakeIntensity = 0;
      }
    }

    if (this.bannerAlpha > 0) {
      this.bannerAlpha -= dt * 0.42;
      this.bannerScale += (1.0 - this.bannerScale) * Math.min(1.0, dt * 7);
      if (this.bannerAlpha < 0) {
        this.bannerAlpha = 0;
      }
    }
  }

  public applyCamera(ctx: CanvasRenderingContext2D): void {
    if (this.shakeTimer > 0) {
      const offsetX = (Math.random() - 0.5) * this.shakeIntensity * (this.shakeTimer / 0.45);
      const offsetY = (Math.random() - 0.5) * this.shakeIntensity * (this.shakeTimer / 0.45);
      ctx.translate(offsetX, offsetY);
    }
  }

  public drawBackground(ctx: CanvasRenderingContext2D, level: LevelConfig): void {
    const customImg = this.customBgs.get(level.bgTheme);
    if (customImg && customImg.complete) {
      ctx.drawImage(customImg, 0, 0, this.virtualWidth, this.virtualHeight);
      return;
    }

    switch (level.bgTheme) {
      case 'nightclub':
        this.drawNightclubBg(ctx);
        break;
      case 'penthouse':
        if (this.cachedPenthouse) {
          ctx.drawImage(this.cachedPenthouse, 0, 0);
        }
        break;
      case 'ibiza':
        if (this.cachedIbiza) {
          ctx.drawImage(this.cachedIbiza, 0, 0);
        }
        break;
    }
  }

  public drawLevelBanner(ctx: CanvasRenderingContext2D): void {
    if (this.bannerAlpha <= 0) return;

    ctx.save();
    ctx.translate(this.virtualWidth / 2, this.virtualHeight * 0.38);
    ctx.scale(this.bannerScale, this.bannerScale);
    ctx.globalAlpha = Math.min(1.0, this.bannerAlpha);

    ctx.fillStyle = 'rgba(7, 0, 14, 0.82)';
    ctx.strokeStyle = this.bannerColor;
    ctx.lineWidth = 2;
    ctx.shadowColor = this.bannerColor;
    ctx.shadowBlur = 18;

    const bannerW = 320;
    const bannerH = 74;
    ctx.beginPath();
    ctx.roundRect(-bannerW / 2, -bannerH / 2, bannerW, bannerH, 18);
    ctx.fill();
    ctx.stroke();

    ctx.shadowBlur = 12;
    ctx.fillStyle = this.bannerColor;
    ctx.font = '900 24px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.bannerText, 0, -10);

    ctx.shadowBlur = 0;
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 12px system-ui, sans-serif';
    ctx.fillText(this.bannerSubtext, 0, 16);

    ctx.restore();
  }

  // --- Pre-render Static Backgrounds to Offscreen Canvases ---

  private rebuildStaticBgCaches(): void {
    const w = this.virtualWidth;
    const h = this.virtualHeight;

    // 1. Nightclub static base (gradient + pole + floor glow)
    this.cachedNightclub = document.createElement('canvas');
    this.cachedNightclub.width = w;
    this.cachedNightclub.height = h;
    const ncCtx = this.cachedNightclub.getContext('2d')!;

    const ncGrad = ncCtx.createLinearGradient(0, 0, 0, h);
    ncGrad.addColorStop(0, '#1c002b');
    ncGrad.addColorStop(0.5, '#0d0016');
    ncGrad.addColorStop(1, '#05000a');
    ncCtx.fillStyle = ncGrad;
    ncCtx.fillRect(0, 0, w, h);

    // Center cabaret stage pole
    ncCtx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ncCtx.fillRect(w / 2 - 2, 0, 4, h * 0.85);

    // Floor glow
    const floorGlow = ncCtx.createRadialGradient(w / 2, h * 0.9, 10, w / 2, h * 0.9, w * 0.7);
    floorGlow.addColorStop(0, 'rgba(255, 0, 128, 0.28)');
    floorGlow.addColorStop(1, 'transparent');
    ncCtx.fillStyle = floorGlow;
    ncCtx.fillRect(0, h * 0.7, w, h * 0.3);

    // 2. Penthouse static cache
    this.cachedPenthouse = document.createElement('canvas');
    this.cachedPenthouse.width = w;
    this.cachedPenthouse.height = h;
    const phCtx = this.cachedPenthouse.getContext('2d')!;

    const phGrad = phCtx.createLinearGradient(0, 0, 0, h);
    phGrad.addColorStop(0, '#040b1e');
    phGrad.addColorStop(0.6, '#081738');
    phGrad.addColorStop(1, '#020610');
    phCtx.fillStyle = phGrad;
    phCtx.fillRect(0, 0, w, h);

    // City skyscraper silhouettes
    phCtx.fillStyle = '#02050f';
    const buildings = [
      { x: 20, w: 60, h: 260 },
      { x: 90, w: 75, h: 360 },
      { x: 180, w: 55, h: 220 },
      { x: 250, w: 80, h: 310 },
      { x: 345, w: 85, h: 280 }
    ];

    for (const b of buildings) {
      phCtx.fillRect(b.x, h * 0.65 - b.h, b.w, b.h);

      phCtx.fillStyle = '#ffecb3';
      phCtx.globalAlpha = 0.45;
      for (let wy = h * 0.65 - b.h + 20; wy < h * 0.65 - 20; wy += 28) {
        for (let wx = b.x + 10; wx < b.x + b.w - 10; wx += 16) {
          if (((wx * 17 + wy * 31) % 10) > 2) {
            phCtx.fillRect(wx, wy, 6, 8);
          }
        }
      }
      phCtx.fillStyle = '#02050f';
      phCtx.globalAlpha = 1.0;
    }

    // Windows columns
    phCtx.fillStyle = '#152238';
    phCtx.fillRect(0, 0, 14, h);
    phCtx.fillRect(w - 14, 0, 14, h);
    phCtx.fillRect(w * 0.33, 0, 8, h * 0.7);
    phCtx.fillRect(w * 0.66, 0, 8, h * 0.7);

    // Jacuzzi glow
    const jacuzziGlow = phCtx.createRadialGradient(w * 0.5, h * 0.88, 20, w * 0.5, h * 0.88, w * 0.8);
    jacuzziGlow.addColorStop(0, 'rgba(0, 240, 255, 0.28)');
    jacuzziGlow.addColorStop(0.7, 'rgba(212, 175, 55, 0.15)');
    jacuzziGlow.addColorStop(1, 'transparent');
    phCtx.fillStyle = jacuzziGlow;
    phCtx.fillRect(0, h * 0.65, w, h * 0.35);

    // 3. Ibiza static cache
    this.cachedIbiza = document.createElement('canvas');
    this.cachedIbiza.width = w;
    this.cachedIbiza.height = h;
    const ibCtx = this.cachedIbiza.getContext('2d')!;

    const ibGrad = ibCtx.createLinearGradient(0, 0, 0, h);
    ibGrad.addColorStop(0, '#2e0854');
    ibGrad.addColorStop(0.3, '#781559');
    ibGrad.addColorStop(0.55, '#c93b2b');
    ibGrad.addColorStop(0.75, '#ff8800');
    ibGrad.addColorStop(1, '#ffc400');
    ibCtx.fillStyle = ibGrad;
    ibCtx.fillRect(0, 0, w, h);

    // Sinking sun
    const sunGrad = ibCtx.createRadialGradient(w * 0.5, h * 0.55, 10, w * 0.5, h * 0.55, 140);
    sunGrad.addColorStop(0, '#ffffff');
    sunGrad.addColorStop(0.35, '#ffdd66');
    sunGrad.addColorStop(0.7, '#ff5500');
    sunGrad.addColorStop(1, 'transparent');
    ibCtx.fillStyle = sunGrad;
    ibCtx.beginPath();
    ibCtx.arc(w * 0.5, h * 0.55, 140, 0, Math.PI * 2);
    ibCtx.fill();

    // Sea horizon
    ibCtx.fillStyle = '#1a0526';
    ibCtx.fillRect(0, h * 0.68, w, h * 0.32);

    // Sea reflections
    ibCtx.fillStyle = 'rgba(255, 170, 0, 0.35)';
    for (let y = h * 0.7; y < h; y += 12) {
      const rw = (y - h * 0.68) * 2.5;
      ibCtx.fillRect(w * 0.5 - rw / 2, y, rw, 4);
    }

    // Silhouette Palm Trees
    ibCtx.fillStyle = '#0d0214';
    ibCtx.beginPath();
    ibCtx.moveTo(15, h);
    ibCtx.quadraticCurveTo(w * 0.18, h * 0.5, w * 0.08, h * 0.25);
    ibCtx.lineTo(w * 0.05, h * 0.25);
    ibCtx.quadraticCurveTo(w * 0.12, h * 0.5, 0, h);
    ibCtx.fill();

    this.drawPalmLeavesOn(ibCtx, w * 0.07, h * 0.25);
    this.drawPalmLeavesOn(ibCtx, w * 0.95, h * 0.32);
  }

  private drawNightclubBg(ctx: CanvasRenderingContext2D): void {
    const w = this.virtualWidth;
    const h = this.virtualHeight;

    if (this.cachedNightclub) {
      ctx.drawImage(this.cachedNightclub, 0, 0);
    }

    // Dynamic animated laser spotlights
    ctx.save();
    const beamAngle = Math.sin(this.animTimer * 1.4) * 0.28;
    ctx.globalAlpha = 0.16;

    // Left spotlight (hot pink)
    ctx.fillStyle = '#ff007f';
    ctx.beginPath();
    ctx.moveTo(w * 0.15, 0);
    ctx.lineTo(w * 0.15 + Math.tan(beamAngle - 0.2) * h, h);
    ctx.lineTo(w * 0.15 + Math.tan(beamAngle + 0.2) * h, h);
    ctx.closePath();
    ctx.fill();

    // Right spotlight (cyan)
    ctx.fillStyle = '#00f0ff';
    ctx.beginPath();
    ctx.moveTo(w * 0.85, 0);
    ctx.lineTo(w * 0.85 + Math.tan(-beamAngle - 0.2) * h, h);
    ctx.lineTo(w * 0.85 + Math.tan(-beamAngle + 0.2) * h, h);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  private drawPalmLeavesOn(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
    const leafAngles = [-0.9, -0.4, 0.1, 0.6, 1.1, 1.6];
    for (const a of leafAngles) {
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      const ex = cx + Math.cos(a) * 95;
      const ey = cy + Math.sin(a) * 65 + 25;
      ctx.quadraticCurveTo(cx + Math.cos(a) * 60, cy - 25, ex, ey);
      ctx.quadraticCurveTo(cx + Math.cos(a) * 60, cy + 15, cx, cy);
      ctx.fill();
    }
  }
}
